/*
 * Calculadora de frete (versão 3, independente de tema) usando as tarifas reais do checkout.
 * Temas cobertos: Dawn (pubsub), Prestige/Impact (eventos Maestrooo) e Horizon/Atelier (ThemeEvents).
 * Funciona com Frenet, Melhor Envio ou qualquer transportadora via Carrier Service,
 * porque a cotação vem da própria Shopify em /cart/shipping_rates.json.
 *
 * Modo "carrinho": cota com o carrinho real.
 * Modo "produto": cria um carrinho temporário (trocando o cookie "cart"),
 *                 adiciona a variante selecionada, cota e restaura o cookie original.
 *
 * Atributos:
 *   data-modo="produto|carrinho"
 *   data-form-id="<id do <form> do produto>"  (só no modo produto)
 */
(function () {
  if (customElements.get('calculadora-frete')) return;

  const CHAVE_CEP = 'calculadora_frete_cep';

  class CalculadoraFrete extends HTMLElement {
    connectedCallback() {
      this.modo = this.dataset.modo || 'carrinho';
      this.input = this.querySelector('[data-frete-cep]');
      this.botao = this.querySelector('[data-frete-calcular]');
      this.status = this.querySelector('[data-frete-status]');
      this.lista = this.querySelector('[data-frete-resultado]');
      this.limite = parseInt(this.dataset.limite, 10) || 0;
      this.ocupado = false;
      this.controlador = new AbortController();
      const sinal = this.controlador.signal;

      this.botao.addEventListener('click', () => this.calcular(), { signal: sinal });

      // Botão "ver mais / ver menos" criado junto com a lista.
      this.addEventListener(
        'click',
        (evento) => {
          const alternar = evento.target.closest('[data-frete-mais]');
          if (!alternar) return;
          evento.preventDefault();
          const expandida = this.classList.toggle('calculadora-frete--expandida');
          alternar.textContent = expandida ? 'Ver menos' : alternar.dataset.rotulo;
        },
        { signal: sinal }
      );

      this.input.addEventListener(
        'input',
        () => {
          const digitos = this.input.value.replace(/\D/g, '').slice(0, 8);
          this.input.value = digitos.length > 5 ? digitos.slice(0, 5) + '-' + digitos.slice(5) : digitos;
          if (digitos.length === 8) this.calcular();
        },
        { signal: sinal }
      );

      // Enter calcula e nunca envia o formulário em volta (no drawer, seria o checkout).
      this.input.addEventListener(
        'keydown',
        (evento) => {
          if (evento.key === 'Enter') {
            evento.preventDefault();
            this.calcular();
          }
        },
        { signal: sinal }
      );

      this.escutarMudancas(sinal);

      // Lembra o último CEP digitado e já calcula.
      const salvo = this.lerCepSalvo();
      if (salvo) {
        this.input.value = salvo.slice(0, 5) + '-' + salvo.slice(5);
        this.calcular();
      }
    }

    disconnectedCallback() {
      this.controlador.abort();
      if (this.cancelarAssinatura) this.cancelarAssinatura();
    }

    // Recalcula quando a variante muda (produto) ou o carrinho muda (carrinho).
    // Cobre Dawn (pubsub) e Prestige/Impact/Focal (eventos DOM da Maestrooo).
    escutarMudancas(sinal) {
      const recalcular = () => {
        // Se o tema re-renderizou o campo e apagou o CEP, recupera o último salvo.
        if (this.input.value.replace(/\D/g, '').length !== 8) {
          const salvo = this.lerCepSalvo();
          if (!salvo) return;
          this.input.value = salvo.slice(0, 5) + '-' + salvo.slice(5);
        }
        this.calcular();
      };

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const evento = this.modo === 'produto' ? PUB_SUB_EVENTS.variantChange : PUB_SUB_EVENTS.cartUpdate;
        this.cancelarAssinatura = subscribe(evento, recalcular);
      }

      if (this.modo === 'produto') {
        document.addEventListener(
          'variant:change',
          (evento) => {
            const formId = evento.detail && evento.detail.formId;
            if (!formId || formId === this.dataset.formId) recalcular();
          },
          { signal: sinal }
        );
        // Horizon / Atelier
        document.addEventListener('variant:update', () => recalcular(), { signal: sinal });
      } else {
        document.addEventListener('cart:change', recalcular, { signal: sinal });
        document.addEventListener('cart:refresh', recalcular, { signal: sinal });
        // Horizon / Atelier
        document.addEventListener('cart:update', recalcular, { signal: sinal });
      }
    }

    mostrar(mensagem, erro) {
      this.status.textContent = mensagem || '';
      this.status.classList.toggle('calculadora-frete__status--erro', Boolean(erro));
    }

    async calcular() {
      const cep = this.input.value.replace(/\D/g, '');
      if (cep.length !== 8) {
        this.mostrar('Digite um CEP válido com 8 números.', true);
        return;
      }
      if (this.ocupado) return;

      this.ocupado = true;
      this.classList.add('calculadora-frete--carregando');
      this.lista.innerHTML = '';
      this.mostrar('Calculando...');

      try {
        const uf = await this.buscarUf(cep);
        const tarifas = this.modo === 'produto' ? await this.cotarProduto(cep, uf) : await this.cotar(cep, uf);
        this.salvarCep(cep);
        this.renderizar(tarifas);
      } catch (erro) {
        if (erro.message === 'cep') {
          this.mostrar('CEP não encontrado.', true);
        } else if (erro.message === 'variante') {
          this.mostrar('Selecione uma opção do produto para calcular o frete.', true);
        } else if (erro.message === 'isolamento') {
          this.mostrar('Adicione o produto ao carrinho para calcular o frete.', true);
        } else {
          this.mostrar('Não foi possível calcular o frete agora. Tente novamente.', true);
        }
      } finally {
        this.ocupado = false;
        this.classList.remove('calculadora-frete--carregando');
      }
    }

    // A Shopify exige a UF junto com o CEP. ViaCEP primeiro, BrasilAPI como reserva.
    async buscarUf(cep) {
      try {
        const resposta = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const dados = await resposta.json();
        if (!dados.erro && dados.uf) return dados.uf;
      } catch (erro) {
        /* tenta a reserva */
      }
      try {
        const resposta = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
        if (resposta.ok) {
          const dados = await resposta.json();
          if (dados.state) return dados.state;
        }
      } catch (erro) {
        /* cai no erro abaixo */
      }
      throw new Error('cep');
    }

    // Apps de upsell, brinde e "produto adicionado" sobrescrevem window.fetch e XMLHttpRequest
    // para vigiar /cart/add.js. Um iframe vazio tem o fetch original, invisível para eles.
    fetchLimpo(caminho, opcoes) {
      if (!CalculadoraFrete.quadro) {
        const quadro = document.createElement('iframe');
        quadro.setAttribute('aria-hidden', 'true');
        quadro.tabIndex = -1;
        quadro.style.display = 'none';
        document.body.appendChild(quadro);
        CalculadoraFrete.quadro = quadro;
      }
      const janela = CalculadoraFrete.quadro.contentWindow;
      const url = new URL(caminho, location.origin).toString();
      const executar = janela && janela.fetch ? janela.fetch : fetch;
      return executar(url, Object.assign({ credentials: 'same-origin' }, opcoes || {}));
    }

    async cotar(cep, uf) {
      const parametros = new URLSearchParams({
        'shipping_address[zip]': cep,
        'shipping_address[country]': 'BR',
        'shipping_address[province]': uf,
      });
      const resposta = await this.fetchLimpo(`/cart/shipping_rates.json?${parametros}`);
      if (!resposta.ok) throw new Error('tarifas');
      const dados = await resposta.json();
      return dados.shipping_rates || [];
    }

    // Lê variante e quantidade pelo formulário, incluindo campos ligados por form="...".
    lerSelecaoProduto() {
      const formulario = document.getElementById(this.dataset.formId);
      if (!formulario) return null;
      const campos = Array.from(formulario.elements);
      const camposId = campos.filter((campo) => campo.name === 'id' && campo.value);
      const campoId = camposId.find((campo) => !campo.disabled) || camposId[0];
      if (!campoId) return null;
      const campoQuantidade = campos.find((campo) => campo.name === 'quantity');
      return {
        id: Number(campoId.value),
        quantidade: parseInt(campoQuantidade && campoQuantidade.value, 10) || 1,
      };
    }

    async cotarProduto(cep, uf) {
      const selecao = this.lerSelecaoProduto();
      if (!selecao) throw new Error('variante');

      // Lê o carrinho real antes de qualquer coisa (se não existir, a Shopify cria um vazio aqui).
      const leituraReal = await this.fetchLimpo('/cart.js');
      if (!leituraReal.ok) throw new Error('isolamento');
      const carrinhoReal = await leituraReal.json();

      const cookieOriginal = this.lerCookieCarrinho();
      this.gravarCookieCarrinho(this.tokenAleatorio());
      try {
        // Garantia: só adiciona se a Shopify estiver mesmo usando um carrinho separado do real
        // (vazio e com token diferente). Se não conseguir isolar, aborta sem tocar no carrinho real.
        const verificacao = await this.fetchLimpo('/cart.js');
        if (!verificacao.ok) throw new Error('isolamento');
        const carrinhoTemporario = await verificacao.json();
        const isolado = carrinhoTemporario.item_count === 0 && carrinhoTemporario.token !== carrinhoReal.token;
        if (!isolado) throw new Error('isolamento');

        const resposta = await this.fetchLimpo('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: selecao.id, quantity: selecao.quantidade }] }),
        });
        if (!resposta.ok) throw new Error('add');
        return await this.cotar(cep, uf);
      } finally {
        if (cookieOriginal) this.gravarCookieCarrinho(cookieOriginal);
        else this.apagarCookieCarrinho();
      }
    }

    renderizar(tarifas) {
      if (!tarifas.length) {
        this.mostrar('Nenhuma opção de frete disponível para este CEP.', true);
        return;
      }
      this.mostrar('');
      this.classList.remove('calculadora-frete--expandida');
      const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
      const ocultas = this.limite > 0 ? Math.max(0, tarifas.length - this.limite) : 0;

      const itens = tarifas.map((tarifa, indice) => {
        const preco = parseFloat(tarifa.price) || 0;
        const gratis = preco === 0;
        const extra = ocultas > 0 && indice >= this.limite;

        // Retirada na loja costuma vir como "Retirar|Endereço": nome na primeira parte, resto no detalhe.
        const partes = String(tarifa.name || '').split('|');
        const nome = partes.shift().trim();
        const detalhe = [partes.join(' ').trim(), this.prazo(tarifa)].filter(Boolean).join(' · ');

        return (
          `<li class="calculadora-frete__item${extra ? ' calculadora-frete__item--extra' : ''}">` +
          `<span class="calculadora-frete__nome">${this.escapar(nome)}</span>` +
          `<span class="calculadora-frete__prazo">${this.escapar(detalhe)}</span>` +
          `<span class="calculadora-frete__valor${gratis ? ' calculadora-frete__valor--gratis' : ''}">` +
          (gratis ? 'Grátis' : moeda.format(preco)) +
          '</span></li>'
        );
      });

      if (ocultas > 0) {
        const rotulo = `Ver mais ${ocultas} ${ocultas === 1 ? 'opção' : 'opções'}`;
        itens.push(
          '<li class="calculadora-frete__mais">' +
          `<button type="button" class="calculadora-frete__mais-botao" data-frete-mais data-rotulo="${this.escapar(rotulo)}">${this.escapar(rotulo)}</button>` +
          '</li>'
        );
      }

      this.lista.innerHTML = itens.join('');
    }

    prazo(tarifa) {
      const dias = Array.isArray(tarifa.delivery_days)
        ? tarifa.delivery_days[tarifa.delivery_days.length - 1]
        : tarifa.delivery_days;
      const n = parseInt(dias, 10);
      if (!isNaN(n) && n > 0) return `até ${n} ${n === 1 ? 'dia útil' : 'dias úteis'}`;
      if (tarifa.delivery_date) {
        const [ano, mes, dia] = tarifa.delivery_date.split('-');
        return `previsão ${dia}/${mes}/${ano}`;
      }
      return '';
    }

    escapar(texto) {
      return String(texto == null ? '' : texto).replace(/[&<>"']/g, (c) => {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    lerCepSalvo() {
      try {
        return localStorage.getItem(CHAVE_CEP) || '';
      } catch (erro) {
        return '';
      }
    }

    salvarCep(cep) {
      try {
        localStorage.setItem(CHAVE_CEP, cep);
      } catch (erro) {
        /* navegação privada: ignora */
      }
    }

    lerCookieCarrinho() {
      const encontrado = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
      return encontrado ? encontrado[1] : '';
    }

    gravarCookieCarrinho(valor) {
      const validade = new Date();
      validade.setTime(validade.getTime() + 14 * 864e5);
      document.cookie = `cart=${valor}; expires=${validade.toUTCString()}; path=/; SameSite=Lax`;
    }

    apagarCookieCarrinho() {
      document.cookie = 'cart=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    }

    tokenAleatorio() {
      return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
    }
  }

  customElements.define('calculadora-frete', CalculadoraFrete);
})();
